/**
 * routes-config.tsx - Route table for vite-react-ssg.
 *
 * Day la mot dang routes config RouteRecord[] de vite-react-ssg co the
 * prerender build-time. App layout shell (Header/Footer/ConsentBanner) di
 * vao Layout component, mounted o root route. Tat ca route hien co trong
 * `app.tsx` chuyen sang day, KHONG doi semantic.
 *
 * Prerender scope (xem `vite.config.ts` ssgOptions.includedRoutes):
 *   - /blog (index list)
 *   - /blog/<slug> 36 article
 *   - /blog/cluster/<cluster> (defer - chua co route hien tai)
 *   - Tat ca route con lai van work voi SPA hydration (client-only).
 *
 * Auth/dashboard/chat routes EXCLUDE khoi prerender vi co `useAuth()` +
 * client-only state. Static HTML cho route do se la "loading" snapshot
 * KHONG huu ich cho SEO.
 */

import type { RouteRecord } from 'vite-react-ssg';
import { Navigate, useNavigationType } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Layout } from './layout';
import { getStoredLang, useGeoCountry } from './use-lang';
import { LandingRoute } from './routes/landing';
import { useAuth } from './use-auth';
import { TransparencyRoute } from './routes/transparency';
import { ChatRoute } from './routes/chat';
import { ReportIssueRoute } from './routes/ReportIssue';
import { TransparencyM1Route } from './routes/transparency-m1';
import { ShortcutCatalogRoute } from './routes/shortcut-catalog';
import { DocPackCatalogRoute } from './routes/docpack-catalog';
import { LoginRoute, SignupRoute } from './routes/auth';
import { AuthCallbackRoute } from './routes/auth-callback';
import { MeRoute } from './routes/me';
import { ShortcutDetailRoute } from './routes/shortcut-detail';
import { DocPackDetailRoute } from './routes/docpack-detail';
import { PrivacyRoute, TermsRoute } from './routes/static-legal';
import { NotFoundRoute } from './routes/not-found';
import { AboutRoute } from './routes/about';
import { ContactRoute } from './routes/contact';
import { EditorialStandardsRoute } from './routes/editorial-standards';
import { NewsFeedRoute } from './routes/news-feed';
import { NewsDetailRoute } from './routes/news-detail';
import { BlogFeedRoute } from './routes/blog-feed';
import { BlogDetailRoute, getBlogStaticPaths } from './routes/blog-detail';
import { CorpusSearchRoute } from './routes/corpus-search';
import { CorpusDetailRoute } from './routes/corpus-detail';
import { DashboardSocialRoute } from './routes/dashboard/Social';
import { DashboardHubRoute } from './routes/dashboard/Hub';
import { AgentDashboardRoute } from './routes/dashboard/Agents';
import { DashboardContentRoute } from './routes/dashboard/Content';
import { DashboardSeoRoute } from './routes/dashboard/Seo';
import { LandingEditorialRoute } from './routes/landing-editorial';

/**
 * HomeGate: authed user vao `/` lan dau (browser URL / refresh) -> redirect /chat.
 * Click Link "Trang chu" trong app (PUSH) -> hien landing binh thuong.
 * Anon -> luon landing.
 *
 * SSG note: prerender lan ban dau (build-time) chay isAuthenticated=false +
 * navType non-POP -> render LandingRoute. Khi hydrate tren browser, neu
 * user da login va navType=POP, redirect /chat. Static HTML van seo-friendly
 * (LandingRoute content).
 */
function HomeGate({ lang }: { lang: 'en' | 'vi' }) {
  const { isAuthenticated } = useAuth();
  const navType = useNavigationType();
  const consumedRef = useRef(false);
  const geoCountry = useGeoCountry();
  const [geoRedirect, setGeoRedirect] = useState(false);

  // Geo default (DECISIONS s2026-06-12-GEO-DEFAULT-LANG): visitor VN vao `/`
  // (EN landing) chua co explicit lang override -> chuyen `/vi/`. Chay qua
  // effect state (post-hydration) de SSG HTML EN khong mismatch (React #418).
  // `/vi/` khong redirect nguoc (URL explicit = intent). Bot non-VN giu EN.
  useEffect(() => {
    if (lang === 'en' && geoCountry === 'VN' && getStoredLang() === null) {
      setGeoRedirect(true);
    }
  }, [lang, geoCountry]);

  // Guard `lang === 'en'` BAT BUOC: React Router reuse cung instance HomeGate
  // khi `/` (lang=en) -> `/vi/` (lang=vi) (cung component type + vi tri Outlet),
  // nen state `geoRedirect` dinh lai. Neu khong guard, instance /vi/ van render
  // <Navigate to="/vi/"> -> redirect loop self -> content KHONG BAO GIO mount
  // -> trang blank (BUG-2026-06-12-LANG-REDIRECT-BLANK, repro Playwright scenario A).
  if (geoRedirect && lang === 'en') {
    return <Navigate to="/vi/" replace />;
  }

  if (isAuthenticated && navType === 'POP' && !consumedRef.current) {
    consumedRef.current = true;
    return <Navigate to="/chat" replace />;
  }
  return <LandingEditorialRoute lang={lang} />;
}

export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <Layout />,
    entry: 'src/layout.tsx',
    children: [
      { index: true, element: <HomeGate lang="en" /> },
      { path: 'vi', element: <HomeGate lang="vi" /> },
      { path: 'expat', element: <LandingRoute slug="expat" /> },
      { path: 'sme', element: <LandingRoute slug="sme" /> },
      { path: 'citizen', element: <LandingRoute slug="citizen" /> },
      { path: 'transparency', element: <TransparencyRoute /> },
      { path: 'privacy', element: <PrivacyRoute /> },
      { path: 'terms', element: <TermsRoute /> },
      { path: 'chat', element: <ChatRoute /> },
      { path: 'ask', element: <Navigate to="/chat" replace /> },
      { path: 'transparency-m1', element: <TransparencyM1Route /> },
      { path: 'report-issue', element: <ReportIssueRoute /> },
      { path: 'shortcut', element: <ShortcutCatalogRoute /> },
      { path: 'shortcut/:slug', element: <ShortcutDetailRoute /> },
      { path: 'docpack', element: <DocPackCatalogRoute /> },
      { path: 'docpack/:sku', element: <DocPackDetailRoute /> },
      { path: 'auth/login', element: <LoginRoute /> },
      { path: 'auth/signup', element: <SignupRoute /> },
      { path: 'auth/callback', element: <AuthCallbackRoute /> },
      { path: 'me', element: <MeRoute /> },
      { path: 'about', element: <AboutRoute /> },
      { path: 'contact', element: <ContactRoute /> },
      { path: 'editorial-standards', element: <EditorialStandardsRoute /> },
      { path: 'news', element: <NewsFeedRoute variant="all" /> },
      { path: 'news/topic/:topic', element: <NewsFeedRoute variant="topic" /> },
      { path: 'news/source/:source', element: <NewsFeedRoute variant="source" /> },
      { path: 'news/tag/:tag', element: <NewsFeedRoute variant="tag" /> },
      { path: 'news/:slug', element: <NewsDetailRoute /> },
      { path: 'blog', element: <BlogFeedRoute /> },
      {
        path: 'blog/:slug',
        element: <BlogDetailRoute />,
        // Dynamic blog routes prerender 36 article. Wildcard `/blog/*`
        // van hoat dong (cluster index, fallback) qua client SPA route
        // ben duoi (see blog/* below).
        getStaticPaths: getBlogStaticPaths,
      },
      // Wildcard cho cluster index + slug fallback (SPA-only).
      { path: 'blog/*', element: <BlogDetailRoute /> },
      { path: 'tim-kiem', element: <CorpusSearchRoute /> },
      { path: 'van-ban/:id', element: <CorpusDetailRoute /> },
      { path: 'van-ban/:id/:tab', element: <CorpusDetailRoute /> },
      { path: 'dashboard', element: <DashboardHubRoute /> },
      { path: 'dashboard/social', element: <DashboardSocialRoute /> },
      { path: 'dashboard/agents', element: <AgentDashboardRoute /> },
      { path: 'dashboard/content', element: <DashboardContentRoute /> },
      { path: 'dashboard/seo', element: <DashboardSeoRoute /> },
      { path: '*', element: <NotFoundRoute /> },
    ],
  },
];
